import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { getImageLink } from "../../../utils/images";

// Delivery HTML-escapes richText ("→&quot;, <→&lt;), so additionalDescription arrives escaped.
// Decode via a detached textarea before injecting it as HTML.
const decodeHtml = (s) => {
    if (typeof s !== "string" || !s) return "";
    const t = document.createElement("textarea");
    t.innerHTML = s;
    return t.value;
};

// Minimal sanitizer for editor rich text (mirrors the FTL theme's sanitizeHtml): strip active
// elements and any on*/javascript: handlers before injecting the fragment. The source is
// author-only CMS richtext, but decode-then-inject would otherwise let event-handler attributes
// (e.g. <img onerror>) through — so scrub them regardless.
const sanitizeHtml = (html) => {
    if (!html) return "";
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    tpl.content.querySelectorAll("script, style, iframe, object, embed").forEach((n) => n.remove());
    tpl.content.querySelectorAll("*").forEach((el) => {
        [...el.attributes].forEach((attr) => {
            const name = attr.name.toLowerCase();
            const val = (attr.value || "").trim().toLowerCase();
            if (name.startsWith("on") || val.startsWith("javascript:")) el.removeAttribute(attr.name);
        });
    });
    return tpl.innerHTML;
};

// model-viewer is a web component (Google) for GLB/glTF + AR. Load the self-contained CDN build
// (it bundles three.js internally — bundling the npm package instead would drag a ~1 MB three.js
// peer-dep into the app for a feature that's usually dormant). Pinned to a fixed version rather
// than "latest" to avoid pulling an unexpected release. Only injected when a model is present.
// (If this app ever gets a strict CSP, allow this origin or switch to a bundled/self-hosted copy.)
const MODEL_VIEWER_SRC = "https://unpkg.com/@google/model-viewer@4.0.0/dist/model-viewer.min.js";
const ensureModelViewer = () => {
    if (typeof window === "undefined") return;
    if (window.customElements && window.customElements.get("model-viewer")) return;
    if (document.querySelector("script[data-model-viewer]")) return;
    const s = document.createElement("script");
    s.type = "module";
    s.src = MODEL_VIEWER_SRC;
    s.setAttribute("data-model-viewer", "true");
    document.head.appendChild(s);
};

// The delivery endpoint expands the dam links into *Reference nodes (see the endpoint's
// referenceResolver config); fall back to the raw property if the reference is absent.
const assetLink = (node) => (node ? getImageLink(node) : null);

const ProductDecoration = ({ decoration }) => {
    const glb = assetLink(decoration.modelGlbReference);
    const usdz = assetLink(decoration.modelUsdzReference);
    const description = sanitizeHtml(decodeHtml(decoration.additionalDescription));
    // Additional images are folded into the product image gallery (see the PDP component), so this
    // block renders only the 3D/AR model and the extra description.

    useEffect(() => {
        if (glb) ensureModelViewer();
    }, [glb]);

    if (!glb && !description) return null;

    return (
        <div className="product-decoration-area pb-90">
            <div className="container">
                {glb && (
                    <div className="row mb-50">
                        <div className="col-12">
                            <model-viewer
                                src={glb}
                                ios-src={usdz || undefined}
                                alt="3D model"
                                camera-controls=""
                                auto-rotate=""
                                ar=""
                                ar-modes="webxr scene-viewer quick-look"
                                style={{ width: "100%", height: "480px", backgroundColor: "#f7f7f7", borderRadius: "8px" }}
                            ></model-viewer>
                        </div>
                    </div>
                )}

                {description && (
                    <div className="row mb-40">
                        <div className="col-12">
                            <div className="product-decoration-desc" dangerouslySetInnerHTML={{ __html: description }} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

ProductDecoration.propTypes = {
    decoration: PropTypes.object,
};

export default ProductDecoration;
